import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { io as ioClient } from "socket.io-client";
import api, { API_ORIGIN, assetUrl } from "../api";
import {
  Search, Calendar, Clock, MoreVertical, Check, X, Eye, Pencil, Layers, CheckCircle2, XCircle,
} from "lucide-react";
import UserLayout from "../components/Userlayout";
import SafeImage from "../components/SafeImage";
import {
  money, fmtDate, customerQuoteState, pageWindow,
} from "../quoteUtils";

// The quote the customer may see for a row from /requests/mine.
// latestQuotation is the newest version; it's a draft while the admin is editing,
// in which case we hide it (state "updating").
const visibleQuote = (r) => {
  const state = customerQuoteState(r);
  if (state === "awaiting" || state === "updating") return null;
  const q = r.latestQuotation;
  return q && q.status !== "draft" ? q : null;
};

export default function Quotes() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showDateMenu, setShowDateMenu] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);
  const [acting, setActing] = useState(null);
  const menuRef = useRef(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  const formatDuration = (startDate, endDate = new Date()) => {
    if (!startDate) return "—";

    const start = new Date(startDate);
    const end = new Date(endDate);

    let diff = Math.max(0, end.getTime() - start.getTime());

    const totalMinutes = Math.floor(diff / 60000);

    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;

    const parts = [];

    if (days > 0) {
      parts.push(`${days} day${days !== 1 ? "s" : ""}`);
    }

    if (hours > 0) {
      parts.push(`${hours} hr${hours !== 1 ? "s" : ""}`);
    }

    parts.push(`${minutes} min`);

    return parts.join(" ");
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/requests/mine");
      setRequests(Array.isArray(data) ? data : []);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // live: refresh when the supplier sends/updates a quote
  useEffect(() => {
    if (!user?.id) return;
    const socket = ioClient(API_ORIGIN, { transports: ["websocket", "polling"] });
    socket.emit("join", `user:${user.id}`);
    socket.on("quotes:update", load);
    socket.on("notification:new", (n) => { if (n?.type === "quote_sent") load(); });
    return () => socket.disconnect();
  }, [user?.id, load]);

  useEffect(() => {
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(null); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const rows = useMemo(() => requests.map((r) => ({ ...r, _state: customerQuoteState(r), _quote: visibleQuote(r) })), [requests]);

  // "Pending" = not decided yet: awaiting a quote, quote sent, being updated, or revision asked for
  const PENDING_STATES = ["awaiting", "sent", "updating", "revision_requested"];
  const matchesTab = (r, key) => (key === "all" ? true : key === "pending" ? PENDING_STATES.includes(r._state) : r._state === key);

  const counts = useMemo(() => {
    const c = { all: rows.length, pending: 0, approved: 0, declined: 0 };
    rows.forEach((r) => {
      if (PENDING_STATES.includes(r._state)) c.pending += 1;
      else if (c[r._state] !== undefined) c[r._state] += 1;
    });
    return c;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const filtered = useMemo(() => {
    let list = rows.filter((r) => matchesTab(r, stateFilter));
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((r) =>
        [r.requestId, r.quoteId, r.partRequired, r.partNumber, r.notes, r._quote?.parts?.[0]?.name]
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(q))
      );
    }
    const dateOf = (r) => new Date(r._quote ? r.quotedAt : r.createdAt);
    if (dateFrom) list = list.filter((r) => dateOf(r) >= new Date(dateFrom));
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      list = list.filter((r) => dateOf(r) <= to);
    }
    return list;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, stateFilter, search, dateFrom, dateTo]);

  useEffect(() => { setPage(1); }, [search, stateFilter, dateFrom, dateTo, limit]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const pageItems = filtered.slice((page - 1) * limit, page * limit);

  const respond = async (r, status) => {
    setMenuOpen(null);
    const q = r._quote;
    if (!q) return;
    const verb = status === "approved" ? "approve" : "decline";
    if (!window.confirm(`Are you sure you want to ${verb} quote ${q.quoteId} for ${money(q.pricing?.total)}?`)) return;
    setActing(r._id);
    try {
      await api.patch(`/quotations/${q._id}/status`, { status });
      await load();
    } catch (e) {
      alert(e.response?.data?.message || `Could not ${verb} the quote.`);
    } finally {
      setActing(null);
    }
  };

  const thumb = (r) => r._quote?.parts?.[0]?.image || r.partPhotos?.[0] || r.vehicleImage || "";

  return (
    <UserLayout>
      <main className="min-h-[calc(100vh-92px)] bg-[#f7f7f9] pb-14">
        <section className="relative h-[184px] overflow-hidden bg-img3">
          {/* <div className="absolute inset-0 bg-[radial-gradient(circle_at_67%_45%,rgba(240,34,48,.22),transparent_24%),radial-gradient(circle_at_87%_65%,rgba(29,94,255,.25),transparent_26%)]" /> */}
          {/* <div className="absolute -right-20 top-8 h-[2px] w-[62%] rotate-[-9deg] bg-gradient-to-r from-transparent via-red-600 to-blue-500" /> */}
          <div className="agp-page-wrap relative z-10 flex h-full items-center">
            <div><h1 className="text-[23px] font-semibold text-white">My Quotes</h1><p className="mt-2 text-[14px] text-white/90">Search your quotes by quote ID, item or part number and view results instantly.</p></div>
          </div>
        </section>

        <div className="agp-page-wrap -mt-0 pt-7">
          <section className="rounded-[12px] bg-white p-6">
            <div className="grid gap-5 md:grid-cols-[1.65fr_1fr_1fr]">
              <div className="relative"><Search size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-[#777]"/><input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Search by Quote ID, Item or Part Number..." className="h-[44px] w-full rounded-[6px] bg-[#f8f8fa] pl-14 pr-4 text-[13px] outline-none"/></div>
              <div className="relative">
                <button onClick={()=>setShowDateMenu((v)=>!v)} className="flex h-[44px] w-full items-center gap-4 rounded-[6px] bg-[#f8f8fa] px-5 text-left text-[13px] text-[#737780]"><Calendar size={17}/>{dateFrom || dateTo ? `${dateFrom || '…'} – ${dateTo || '…'}` : 'Select Date'}</button>
                {showDateMenu && <div className="absolute right-0 top-[50px] z-30 w-64 rounded-xl border bg-white p-4 shadow-xl"><label className="mb-3 block text-xs">From<input type="date" value={dateFrom} onChange={(e)=>setDateFrom(e.target.value)} className="mt-1 w-full rounded-lg border p-2"/></label><label className="block text-xs">To<input type="date" value={dateTo} onChange={(e)=>setDateTo(e.target.value)} className="mt-1 w-full rounded-lg border p-2"/></label><button onClick={()=>setShowDateMenu(false)} className="mt-3 text-xs font-semibold text-blue-600">Done</button></div>}
              </div>
              <button className="h-[44px] rounded-[7px] bg-[#0d315e] text-[13px] font-semibold text-white">Search</button>
            </div>
          </section>

          <section role="tablist" className="mt-4 grid overflow-hidden rounded-[10px] bg-white md:grid-cols-4">
            {[["all","All Quotes",Layers,""],["pending","Pending",CheckCircle2,""],["approved","Approved",Clock,""],["declined","Declined",XCircle,"text-[#e5303b]"]].map(([k,label,Icon,iconCls])=>{
              const active = stateFilter===k;
              return <button key={k} role="tab" aria-selected={active} onClick={()=>setStateFilter(k)} className={`m-[10px] flex h-[50px] items-center justify-center gap-3 rounded-[8px] text-[15px] transition ${active?'bg-[#0e315e] font-semibold text-white':'text-[#71757d] hover:bg-slate-50'}`}>
                <Icon size={18} className={active ? "" : iconCls} />{label}{counts[k] ? <span className="text-[11px] opacity-70">{counts[k]}</span>:null}
              </button>;
            })}
          </section>

          <section className="mt-4 space-y-4">
            {loading && <div className="rounded-xl bg-white py-16 text-center text-slate-400">Loading…</div>}
            {!loading && pageItems.map((r)=>{
              const q=r._quote; const state=r._state; const img=thumb(r); const itemName=q?.parts?.[0]?.name || r.partRequired || r.notes || 'Part request'; const itemSub=q?.parts?.[0]?.partNumber || r.partNumber || ''; const dateToShow=q ? r.quotedAt : r.createdAt;
              return <article key={r._id} onClick={()=>navigate(`/quotes/${r._id}`)} className="relative cursor-pointer rounded-[4px] bg-white px-5 py-3 transition hover:shadow-md">
                <div className="grid items-center gap-4 md:grid-cols-[24px_104px_1.65fr_190px_190px_170px_34px]">
                  <span className="h-5 w-5 rounded-[6px] border border-[#eceef1] bg-[#fafafa]" />
                  <div className="grid h-[94px] place-items-center overflow-hidden rounded-[8px] bg-[#f5f3ff]"><SafeImage src={img ? assetUrl(img) : ""} alt={itemName} className="h-full w-full object-contain p-3" /></div>
                  <div><div className="flex items-center gap-3 text-[13px] font-semibold text-[#233249]"><span>{q?.quoteId || r.requestId || 'Quote'}</span>{r.requestId && <span className="rounded-full bg-[#f0f1ff] px-3 py-1 text-[10px] font-medium text-[#5971ff]">{r.requestId}</span>}</div><h3 className="mt-3 text-[21px] font-semibold text-[#122039]">{itemName}</h3><p className="mt-1 text-[13px] text-[#858891]">{itemSub || 'Part request'}</p></div>
                  <div className="rounded-[9px] bg-[#f5f8fd] px-6 py-4"><div className="flex gap-3"><Calendar size={18} className="mt-1 text-[#176eff]"/><div><span className="text-[10px] uppercase text-[#7a808b]">Quote Date</span><strong className="mt-1 block text-[14px] text-[#172238]">{fmtDate(dateToShow)}</strong></div></div></div>
                  <div className="rounded-[9px] bg-[#f5f8fd] px-6 py-4"><div className="flex gap-3"><Clock size={18} className="mt-1 text-[#176eff]"/><div><span className="text-[10px] uppercase text-[#7a808b]">Response Time</span><strong className="mt-1 block text-[14px] text-[#172238]">{q ? formatDuration(r.createdAt,r.quotedAt) : formatDuration(r.createdAt,now)}</strong></div></div></div>
                  <div><span className="text-[10px] uppercase text-[#7a808b]">Quote Price</span><strong className="mt-2 block text-[15px] text-[#06953a]">{q ? `${money(q.pricing?.total)} AUD` : 'Awaiting Quote'}</strong></div>
                  <div className="relative" onClick={(e)=>e.stopPropagation()}><button onClick={()=>setMenuOpen(menuOpen===r._id?null:r._id)} className="grid h-8 w-8 place-items-center rounded-lg border text-slate-500"><MoreVertical size={17}/></button>{menuOpen===r._id && <div ref={menuRef} className="absolute right-0 top-10 z-40 w-48 rounded-xl border bg-white py-2 shadow-xl">{q && state==='sent' && <><button onClick={()=>respond(r,'approved')} disabled={acting===r._id} className="flex w-full items-center gap-2 px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"><Check size={15} className="text-green-600"/>Approve Quote</button><button onClick={()=>respond(r,'declined')} disabled={acting===r._id} className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-slate-50 disabled:opacity-50"><X size={15}/>Decline Quote</button><button onClick={()=>navigate(`/quotes/${r._id}`, { state: { revise: true } })} className="flex w-full items-center gap-2 px-4 py-2 text-sm hover:bg-slate-50"><Pencil size={15}/>Revise Quote</button></>}{q && state!=='sent' && <button onClick={()=>navigate(`/quotes/${r._id}`)} className="flex w-full items-center gap-2 px-4 py-2 text-sm hover:bg-slate-50"><Eye size={15}/>View Quote</button>}{!q && <button onClick={()=>navigate(`/quotes/${r._id}`)} className="flex w-full items-center gap-2 px-4 py-2 text-sm hover:bg-slate-50"><Eye size={15}/>View Request</button>}</div>}</div>
                </div>
              </article>;
            })}
            {!loading && pageItems.length===0 && <div className="rounded-xl bg-white py-16 text-center text-slate-400">No quotes match your filters.</div>}
          </section>

          {totalPages>1 && <div className="mt-6 flex justify-center gap-2">{pageWindow(page,totalPages).map((p)=><button key={p} onClick={()=>setPage(p)} className={`h-9 w-9 rounded-lg text-sm ${p===page?'bg-[#0e315e] text-white':'bg-white text-slate-600'}`}>{p}</button>)}</div>}
        </div>
      </main>
    </UserLayout>
  );
}
